/**
 * SentenceDisplay Component
 * Displays sentence-by-sentence learning content with:
 * - Bilingual text (English + Chinese)
 * - Clickable vocabulary words
 * - Grammar points and cultural notes
 * - Speaking strategy (chunking)
 * - Key vocabulary highlighting
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AnnotatedSentence,
  ComprehensiveVocabularyEntry,
  SpeakingChunk,
  DependencyGraphData
} from '../../types/creator-workflow';
import type { WordAnnotation } from '../../services/cefrKnowledgeService';
import { DependencyGraph, DependencyGraphData as DependencyGraphComponentData } from '../dependency-graph';
import { AnnotatedText } from '../AnnotatedText';
import { getTranslation } from '../../utils/getTranslation';
import { useAuth } from '../../contexts/AuthContext';
import VocabularyModal from './VocabularyModal';
import { resolveMediaUrl } from '../../utils/mediaUrl';
import './SentenceDisplay.css';

// Parse annotation string: "[effect startPos endPos startTime endTime]"
interface ParsedAnnotation {
  effect: 'underline' | 'highlight' | 'hide_text' | 'insert';
  startPos: number;
  endPos: number;
  startTime: number;
  endTime: number;
}

const parseAnnotation = (annotation: string): ParsedAnnotation | null => {
  const match = annotation.match(/\[(\w+)\s+(\d+)\s+(\d+)\s+([\d.]+)\s+([\d.]+)\]/);
  if (!match) return null;
  return {
    effect: match[1] as ParsedAnnotation['effect'],
    startPos: parseInt(match[2], 10),
    endPos: parseInt(match[3], 10),
    startTime: parseFloat(match[4]),
    endTime: parseFloat(match[5]),
  };
};

// Tokenize sentence according to rules:
// - Word = 1 token
// - Space = 1 token
// - Punctuation + trailing space = 1 token
const tokenizeSentence = (text: string): string[] => {
  const tokens: string[] = [];
  let i = 0;

  while (i < text.length) {
    const char = text[i];

    // Check if it's punctuation followed by space
    if (/[.,!?;:]/.test(char)) {
      let token = char;
      if (i + 1 < text.length && text[i + 1] === ' ') {
        token += ' ';
        i++;
      }
      tokens.push(token);
      i++;
    }
    // Check if it's a space (standalone)
    else if (char === ' ') {
      tokens.push(' ');
      i++;
    }
    // It's a word character
    else if (/\w/.test(char)) {
      let word = '';
      while (i < text.length && /\w/.test(text[i])) {
        word += text[i];
        i++;
      }
      tokens.push(word);
    }
    // Other characters
    else {
      tokens.push(char);
      i++;
    }
  }

  return tokens;
};

interface SentenceDisplayProps {
  sentence: AnnotatedSentence;
  index: number;
  vocabulary: ComprehensiveVocabularyEntry[];
  additionalActions?: React.ReactNode;
  /** Callback to open edit modal - when provided, shows edit button */
  onEdit?: (index: number) => void;
  /** Callback to regenerate dependency graph for this sentence */
  onRegenerateDependencyGraph?: (index: number) => Promise<void>;
  /** Whether regeneration is in progress */
  isRegenerating?: boolean;
  /** Precise CEFR annotations for this sentence */
  annotations?: WordAnnotation[];
  /** Whether the sentence starts collapsed (default: false) */
  defaultCollapsed?: boolean;
  /** Controlled collapsed state from parent (overrides internal state) */
  collapsed?: boolean;
  /** Callback to add grammar point to notes — shows "+" button when provided.
   *  Receives the sentence's index so the caller can link the note back to the sentence. */
  onAddGrammarNote?: (content: string, tags: string[], sentenceIndex: number) => void;
  /**
   * Override the language used to pick native-language fields from *_natives
   * maps (tips_natives, translations, explanation_natives, etc.).
   *
   * Defaults to `user.languagePreference` (UI locale). For creator-workflow
   * usage, pass `draft.translation_language` so the preview always reflects
   * the draft's chosen translation language, regardless of the creator's UI
   * locale. Published-course views can pass the course's translation language.
   */
  translationLanguage?: string;
}

// Transform API dependency graph data to component format (snake_case → camelCase)
const transformDependencyGraph = (apiData: DependencyGraphData, lang: string): DependencyGraphComponentData | null => {
  // Validate required fields exist and are arrays
  if (!apiData.words || !Array.isArray(apiData.words) ||
      !apiData.arcs || !Array.isArray(apiData.arcs) ||
      !apiData.animation_steps || !Array.isArray(apiData.animation_steps)) {
    console.warn('Invalid dependency graph data: missing or invalid fields', apiData);
    return null;
  }

  return {
    sentence: '', // Not needed for component rendering, will be displayed separately
    words: apiData.words.map(w => ({
      id: w.id,
      word: w.word,
      role: w.role,
      roleZh: getTranslation((w as any).role_natives, lang, w.role_native),
      color: w.color,
    })),
    arcs: apiData.arcs.map(a => ({
      from: a.from,
      to: a.to,
      label: getTranslation((a as any).label_natives, lang, a.label),
      labelEn: a.label_en,
    })),
    animationSteps: apiData.animation_steps.map(s => ({
      nodeIds: s.node_ids,
      explanation: getTranslation((s as any).explanation_natives, lang, s.explanation),
      duration: s.duration,
    })),
  };
};

const SentenceDisplay: React.FC<SentenceDisplayProps> = ({
  sentence,
  index,
  vocabulary,
  additionalActions,
  onEdit,
  onRegenerateDependencyGraph,
  isRegenerating = false,
  annotations,
  defaultCollapsed = false,
  collapsed: controlledCollapsed,
  onAddGrammarNote,
  translationLanguage,
}) => {
  const { t } = useTranslation('creator');
  const { user } = useAuth();
  // Explicit translationLanguage prop wins over UI locale, so creator previews
  // reflect the draft's chosen translation language even if the creator has
  // switched their UI locale. Falls back to user's UI preference, then zh-TW.
  const userLang = translationLanguage || user?.languagePreference || 'zh-TW';
  const [selectedWord, setSelectedWord] = useState<ComprehensiveVocabularyEntry | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isExplanationPlaying, setIsExplanationPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);
  const [addedGrammarNotes, setAddedGrammarNotes] = useState<Set<number>>(new Set());

  // Use controlled state if provided, otherwise internal state
  const isCollapsed = controlledCollapsed !== undefined ? controlledCollapsed : internalCollapsed;
  const toggleCollapsed = () => setInternalCollapsed(prev => !prev);
  const audioRef = useRef<HTMLAudioElement>(null);
  const explanationAudioRef = useRef<HTMLAudioElement>(null);

  // Transform dependency graph data for component
  const dependencyGraphData = useMemo(() => {
    if (!sentence.dependency_graph) return null;
    return transformDependencyGraph(sentence.dependency_graph, userLang);
  }, [sentence.dependency_graph, userLang]);

  // Check if audio is available from sentence object — resolve to full CDN URL
  const effectiveAudioUrl = resolveMediaUrl(sentence.audio_url) || undefined;
  const explanationAudioUrl = resolveMediaUrl(sentence.sentence_analysis?.explanation_audio_url) || undefined;

  // Handle play/pause (memoized for consistency)
  const handlePlayClick = useCallback(() => {
    if (!audioRef.current || !effectiveAudioUrl) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  }, [effectiveAudioUrl, isPlaying]);

  // Handle audio ended (memoized for consistency)
  const handleAudioEnded = useCallback(() => {
    setIsPlaying(false);
  }, []);

  // Cleanup main audio on unmount to prevent memory leak
  useEffect(() => {
    const audio = audioRef.current;
    return () => {
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    };
  }, []);

  // Find vocabulary entry for a word
  const findVocabularyEntry = (word: string): ComprehensiveVocabularyEntry | undefined => {
    const normalizedWord = word.toLowerCase().replace(/[.,!?;:]/g, '');
    return vocabulary.find(v => v.word.toLowerCase() === normalizedWord);
  };

  // Check if word is in key vocabulary list
  const isKeyVocabulary = (word: string): boolean => {
    if (!sentence.key_vocabulary_in_sentence) return false;
    const normalizedWord = word.toLowerCase().replace(/[.,!?;:]/g, '');
    return sentence.key_vocabulary_in_sentence.some(
      kw => kw.toLowerCase() === normalizedWord
    );
  };

  // Render sentence text with clickable vocabulary words
  const renderClickableText = (text: string) => {
    const words = text.split(/\b/); // Split by word boundaries

    return words.map((word, idx) => {
      const vocabEntry = findVocabularyEntry(word);
      const isKey = isKeyVocabulary(word);

      if (vocabEntry) {
        return (
          <span
            key={idx}
            className={`clickable-word ${isKey ? 'key-word' : ''}`}
            onClick={() => setSelectedWord(vocabEntry)}
            title={t('workflow.sentenceDisplay.clickForDefinition')}
          >
            {word}
          </span>
        );
      }

      return <span key={idx}>{word}</span>;
    });
  };

  // Render speaking strategy chunks
  const renderSpeakingStrategy = () => {
    if (!sentence.speaking_strategy) return null;

    return (
      <div className="speaking-strategy">
        <h5>{t('workflow.sentenceDisplay.speakingStrategy')}</h5>
        <div className="chunks-container">
          {sentence.speaking_strategy.chunks.map((chunk: SpeakingChunk, idx: number) => (
            <div key={idx} className="chunk-item">
              <div className="chunk-text">{chunk.text}</div>
              <div className="chunk-translation">{getTranslation(chunk.translations, userLang, chunk.translation)}</div>
              <div className="chunk-focus">{t('workflow.sentenceDisplay.emphasis')} {getTranslation(chunk.focus_natives, userLang, chunk.focus)}</div>
            </div>
          ))}
        </div>
        <div className="speaking-tips">
          <strong>{t('workflow.sentenceDisplay.tips')}</strong> {getTranslation(sentence.speaking_strategy.tips_natives, userLang, sentence.speaking_strategy.tips_native)}
        </div>
      </div>
    );
  };

  // Handle explanation audio playback
  const handleExplanationPlayClick = useCallback(() => {
    if (!explanationAudioRef.current || !explanationAudioUrl) return;

    if (isExplanationPlaying) {
      explanationAudioRef.current.pause();
      setIsExplanationPlaying(false);
    } else {
      explanationAudioRef.current.play();
      setIsExplanationPlaying(true);
    }
  }, [explanationAudioUrl, isExplanationPlaying]);

  // Update current time during playback
  useEffect(() => {
    const audio = explanationAudioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = () => {
      setIsExplanationPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      // Stop playback and reset on unmount to prevent memory leak
      audio.pause();
      audio.currentTime = 0;
    };
  }, []);

  // Get active annotations based on current time
  const getActiveAnnotations = useCallback((time: number): ParsedAnnotation[] => {
    if (!sentence.sentence_analysis?.annotations) return [];

    return sentence.sentence_analysis.annotations
      .map(parseAnnotation)
      .filter((ann): ann is ParsedAnnotation =>
        ann !== null && time >= ann.startTime && time <= ann.endTime
      );
  }, [sentence.sentence_analysis?.annotations]);

  // Render sentence analysis with animated annotations
  const renderSentenceAnalysis = () => {
    if (!sentence.sentence_analysis) return null;

    const { explanation_native } = sentence.sentence_analysis;
    const tokens = tokenizeSentence(sentence.text);
    const activeAnnotations = isExplanationPlaying ? getActiveAnnotations(currentTime) : [];

    // Determine which tokens are currently highlighted (only during playback)
    const getTokenStyle = (tokenIdx: number): string => {
      if (!isExplanationPlaying) return '';

      const classes: string[] = [];
      for (const ann of activeAnnotations) {
        if (tokenIdx >= ann.startPos && tokenIdx <= ann.endPos) {
          classes.push(`annotation-${ann.effect}`);
        }
      }

      return classes.join(' ');
    };

    return (
      <div className="sentence-analysis">
        {/* Header with title and play button */}
        <div className="sentence-analysis-header">
          <h5>
            <svg aria-hidden="true" className="section-heading-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
            {t('workflow.sentenceDisplay.sentenceAnalysis')}
          </h5>
          {explanationAudioUrl && (
            <button
              className={`explanation-play-btn ${isExplanationPlaying ? 'playing' : ''}`}
              onClick={handleExplanationPlayClick}
              title={isExplanationPlaying ? t('workflow.sentenceDisplay.pauseExplanation') : t('workflow.sentenceDisplay.playExplanation')}
            >
              {isExplanationPlaying ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              )}
              <span>{isExplanationPlaying ? t('workflow.sentenceDisplay.pause') : t('workflow.sentenceDisplay.play')}</span>
            </button>
          )}
        </div>

        {/* Animated sentence with annotations */}
        <div className="analysis-sentence-container">
          <div className="analysis-sentence">
            {tokens.map((token, idx) => (
              <span
                key={idx}
                className={`analysis-token ${getTokenStyle(idx)}`}
              >
                {token}
              </span>
            ))}
          </div>
        </div>

        {/* Explanation text */}
        <div className="analysis-explanation">
          <p>{getTranslation(sentence.sentence_analysis?.explanation_natives, userLang, explanation_native)}</p>
        </div>

        {/* Hidden audio element for explanation */}
        {explanationAudioUrl && (
          <audio
            ref={explanationAudioRef}
            src={explanationAudioUrl}
            preload="none"
          />
        )}
      </div>
    );
  };

  const hasAudio = !!effectiveAudioUrl;

  return (
    <div className={`sentence-display ${isCollapsed ? 'collapsed' : ''}`}>
      <div
        className={`sentence-header ${controlledCollapsed === undefined ? 'clickable' : ''}`}
        onClick={controlledCollapsed === undefined ? toggleCollapsed : undefined}
      >
        <span className="sentence-number">#{index + 1}</span>
        {sentence.speaker && (
          <span className="sentence-speaker">{sentence.speaker}</span>
        )}
        {/* Collapsed preview: English + translation as a single text block,
            so header flex row vertically centers the actions against the
            whole block instead of just the English line. */}
        {isCollapsed && (() => {
          const previewTranslation = getTranslation(sentence.translations, userLang, sentence.translation);
          return (
            <div className="sentence-collapsed-block">
              <div className="sentence-collapsed-text">{sentence.text}</div>
              {previewTranslation && (
                <div className="sentence-collapsed-translation">{previewTranslation}</div>
              )}
            </div>
          );
        })()}
        {/* Header actions group */}
        <div className="sentence-header-actions">
          {/* Play button for sentence audio - always shown, disabled if no audio */}
          <button
            type="button"
            className={`sentence-play-btn ${isPlaying ? 'playing' : ''} ${!hasAudio ? 'disabled' : ''}`}
            onClick={(e) => { e.stopPropagation(); handlePlayClick(); }}
            disabled={!hasAudio}
            title={!hasAudio ? t('workflow.sentenceDisplay.audioNotGenerated') : (isPlaying ? t('workflow.sentenceDisplay.pauseSentence') : t('workflow.sentenceDisplay.playSentence'))}
            aria-label={!hasAudio ? t('workflow.sentenceDisplay.audioNotGenerated') : (isPlaying ? t('workflow.sentenceDisplay.pauseSentence') : t('workflow.sentenceDisplay.playSentence'))}
          >
            {isPlaying ? (
              <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
              </svg>
            ) : hasAudio ? (
              <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z"/>
              </svg>
            ) : (
              <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 13H5v-2h14v2z"/>
              </svg>
            )}
          </button>
          {/* Edit button - only shown when onEdit is provided (creator mode) */}
          {onEdit && (
            <button
              type="button"
              className="sentence-edit-btn"
              onClick={(e) => { e.stopPropagation(); onEdit(index); }}
              title={t('workflow.sentenceDisplay.editSentence')}
              aria-label={t('workflow.sentenceDisplay.editSentenceAriaLabel', { index: index + 1 })}
            >
              <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
              </svg>
            </button>
          )}
          {/* Additional actions passed from parent */}
          {additionalActions}
          {/* Collapse/expand control.
              - Uncontrolled: render as a real <button> with aria-expanded so
                keyboard and screen-reader users can toggle the card. Mouse
                users can still click anywhere on the header via the parent
                div's onClick (the button stops propagation to avoid a
                double-toggle).
              - Controlled (collapse state owned by a parent toolbar like
                "Collapse all"): render as a decorative SVG indicator only,
                since individual cards shouldn't toggle themselves. */}
          {controlledCollapsed === undefined ? (
            <button
              type="button"
              className="sentence-chevron-btn"
              onClick={(e) => { e.stopPropagation(); toggleCollapsed(); }}
              aria-expanded={!isCollapsed}
              aria-label={isCollapsed
                ? t('workflow.sentenceDisplay.expandSentenceAriaLabel', { index: index + 1 })
                : t('workflow.sentenceDisplay.collapseSentenceAriaLabel', { index: index + 1 })}
            >
              <svg
                aria-hidden="true"
                className={`sentence-chevron ${isCollapsed ? '' : 'expanded'}`}
                width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          ) : (
            <svg
              aria-hidden="true"
              className={`sentence-chevron ${isCollapsed ? '' : 'expanded'}`}
              width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          )}
        </div>
      </div>

      {/* Hidden audio element */}
      {effectiveAudioUrl && (
        <audio
          ref={audioRef}
          src={effectiveAudioUrl}
          onEnded={handleAudioEnded}
          preload="none"
        />
      )}

      {/* Expanded content */}
      {!isCollapsed && (
        <>
      <div className="sentence-content">
        {/* English text with clickable vocabulary or CEFR annotations */}
        <div className="sentence-english">
          {annotations && annotations.length > 0 ? (
            <AnnotatedText
              text={sentence.text}
              annotations={annotations}
            />
          ) : (
            renderClickableText(sentence.text)
          )}
        </div>

        {/* Chinese translation */}
        <div className="sentence-chinese">
          {getTranslation(sentence.translations, userLang, sentence.translation)}
        </div>
      </div>

      {/* Grammar points */}
      {sentence.grammar_points && sentence.grammar_points.length > 0 && (
        <div className="grammar-points">
          <h5>{t('workflow.sentenceDisplay.grammarPoints')}</h5>
          {sentence.grammar_points.map((point, idx) => {
            const nativeExplanation = getTranslation(point.explanation_natives, userLang, point.explanation_native);
            const nativeExample = getTranslation(point.example_natives, userLang, point.example_native);
            return (
              <div key={idx} className="grammar-point">
                <div className="grammar-explanation">
                  <p><strong>{t('workflow.sentenceDisplay.en')}</strong> {point.explanation_en}</p>
                  <p><strong>{t('workflow.sentenceDisplay.zh')}</strong> {nativeExplanation}</p>
                  <div className="grammar-example">
                    <p><em>{point.example}</em></p>
                    <p className="example-translation">{nativeExample}</p>
                  </div>
                </div>
                {onAddGrammarNote && (
                  <button
                    className={`btn-add-grammar-note ${addedGrammarNotes.has(idx) ? 'added' : ''}`}
                    onClick={() => {
                      if (addedGrammarNotes.has(idx)) return;
                      const content = `${point.explanation_en}\n${nativeExplanation}\n\n${point.example}\n${nativeExample}`;
                      onAddGrammarNote(content, ['grammar'], index);
                      setAddedGrammarNotes(prev => new Set(prev).add(idx));
                    }}
                    title={addedGrammarNotes.has(idx) ? t('workflow.sentenceDisplay.addedToNotes') : t('workflow.sentenceDisplay.addToNotes')}
                    aria-label={t('workflow.sentenceDisplay.addToNotes')}
                    disabled={addedGrammarNotes.has(idx)}
                  >
                    {addedGrammarNotes.has(idx) ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Cultural notes */}
      {sentence.cultural_notes && (
        <div className="cultural-notes">
          <h5>{t('workflow.sentenceDisplay.culturalNotes')}</h5>
          <p><strong>{t('workflow.sentenceDisplay.en')}</strong> {sentence.cultural_notes.note_en}</p>
          <p><strong>{t('workflow.sentenceDisplay.zh')}</strong> {getTranslation(sentence.cultural_notes.note_natives, userLang, sentence.cultural_notes.note_native)}</p>
        </div>
      )}

      {/* Speaking strategy */}
      {renderSpeakingStrategy()}

      {/* Dependency Graph - Visual Sentence Structure.
          No nested collapse: since the parent sentence card already has
          its own collapse mechanism, an inner disclosure would force the
          user to click twice to reach the content. The header here acts
          as a plain section title matching grammar-points / cultural-notes
          / speaking-strategy. */}
      {dependencyGraphData && (
        <div className="dependency-graph-section">
          <div className="dependency-graph-header">
            <h5 className="dependency-graph-title">
              <svg aria-hidden="true" className="section-heading-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="5" r="2" />
                <circle cx="6" cy="19" r="2" />
                <circle cx="18" cy="19" r="2" />
                <line x1="12" y1="7" x2="6" y2="17" />
                <line x1="12" y1="7" x2="18" y2="17" />
              </svg>
              {t('workflow.sentenceDisplay.syntaxStructure')}
            </h5>
            {onRegenerateDependencyGraph && (
              <button
                type="button"
                className={`btn-regenerate-graph ${isRegenerating ? 'loading' : ''}`}
                onClick={() => onRegenerateDependencyGraph(index)}
                disabled={isRegenerating}
                title={t('workflow.sentenceDisplay.regenerateSyntaxTree')}
              >
                {isRegenerating ? t('workflow.sentenceDisplay.regenerating') : t('workflow.sentenceDisplay.regenerate')}
              </button>
            )}
          </div>
          <div className="dependency-graph-content">
            <DependencyGraph
              data={dependencyGraphData}
              showControls={true}
              showLegend={true}
              autoPlay={false}
            />
          </div>
        </div>
      )}

      {/* Sentence analysis with animated annotations (legacy) */}
      {!dependencyGraphData && renderSentenceAnalysis()}

      {/* Key vocabulary indicator */}
      {sentence.key_vocabulary_in_sentence && sentence.key_vocabulary_in_sentence.length > 0 && (
        <div className="key-vocabulary-list">
          <h5>{t('workflow.sentenceDisplay.keyVocabulary')}</h5>
          <div className="key-vocab-tags">
            {sentence.key_vocabulary_in_sentence.map((word, idx) => (
              <span key={idx} className="key-vocab-tag">{word}</span>
            ))}
          </div>
        </div>
      )}
        </>
      )}

      {/* Vocabulary modal */}
      {selectedWord && (
        <VocabularyModal
          vocabulary={selectedWord}
          onClose={() => setSelectedWord(null)}
        />
      )}
    </div>
  );
};

export default SentenceDisplay;
